import React, { useEffect, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';

interface Opponent {
  _id: string;
  name: string;
}

const DetailsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [opponents, setOpponents] = useState<Opponent[]>([]);
  const [gameDetails, setGameDetails] = useState({
    name: '',
    date: '',
    time: '',
    timezone: 'eastern',
    type: '',
    location: '',
    opponentId: '',
  });

  // Check game details edit permission
  const canEditDetails = sessionManager.canPerformAction('games.gameMenu.details', 'edit');

  // Fetch opponents
  useEffect(() => {
    const fetchOpponents = async () => {
      try {
        const clientId = sessionManager.getClientId();
        const response = await api.get(API_URLS.GET_OPPONENTS, {
          params: clientId ? { clientId } : {},
        });
        if (response.data.success) {
          setOpponents(response.data.data || []);
        } else {
          showErrorMsg(response.data.message || 'Failed to fetch opponents');
        }
      } catch (error) {
        console.error('Error fetching opponents:', error);
        showErrorMsg('Failed to fetch opponents');
      }
    };

    fetchOpponents();
  }, []);

  // Fetch game details
  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        const gameId = sessionManager.getGameId();
        if (!gameId) {
          showErrorMsg('Select a game to get started');
          setLoading(false);
          return;
        }

        const response = await api.get(`${API_URLS.GET_GAME_BY_ID}/${gameId}`);
        if (response.data.success) {
          const gameData = response.data.data;
          setGameDetails({
            name: gameData.name || '',
            date: gameData.date || '',
            time: gameData.time || '',
            timezone: gameData.timezone || 'eastern',
            type: gameData.type || '',
            location: gameData.location || '',
            opponentId: gameData.opponentId || '',
          });
        } else {
          showErrorMsg(response.data.message || 'Failed to fetch game details');
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
        showErrorMsg('Failed to fetch game details');
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setGameDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!gameDetails.opponentId) return showErrorMsg('Opponent is required');
    if (!gameDetails.name.trim()) return showErrorMsg('Game name is required');
    if (!gameDetails.date) return showErrorMsg('Date is required');
    if (!gameDetails.time) return showErrorMsg('Time is required');
    if (!gameDetails.type) return showErrorMsg('Game type is required');
    if (!gameDetails.location) return showErrorMsg('Location is required');

    const gameId = sessionManager.getGameId();
    if (!gameId) return showErrorMsg('Select a game to get started');

    try {
      setLoading(true);
      const updateData = {
        _id: gameId,
        opponentId: gameDetails.opponentId,
        name: gameDetails.name,
        date: gameDetails.date,
        time: gameDetails.time,
        timezone: gameDetails.timezone,
        type: gameDetails.type,
        location: gameDetails.location,
        clientId: sessionManager.getClientId(),
        seasonId: sessionManager.getSeasonId(),
      };

      const response = await api.put(API_URLS.UPDATE_GAME, updateData);
      if (response.data.success && response.data.data) {
        showSuccessMsg('Game details updated successfully!');
        sessionManager.setGameId(response.data.data?._id);
        sessionManager.setGameName(response.data.data?.opponent?.name);
      } else {
        showErrorMsg(response.data.message || 'Failed to update game details');
      }
    } catch (error: any) {
      console.error('Error updating game details:', error);
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to update game details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <div className="flex flex-col justify-between text-sd-navy text-2xl sm:text-3x font-bold mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">Game Details</h1>
        <p className="mt-2 text-sm sm:text-base text-sd-navy font-medium max-w-3xl break-all">
          Update the details of your game below.
        </p>
      </div>
      <form onSubmit={handleSave} className="w-full bg-white shadow-md mb-6 mt-4 border border-sd-line">
        <div className="bg-sd-navy text-white text-xl font-semibold px-4 py-2">Game Details</div>
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-row gap-3 text-base font-medium rounded">
            <p className="min-w-24 pt-1 pl-2">Name</p>
            <select
              name="opponentId"
              value={gameDetails.opponentId}
              onChange={handleInputChange}
              disabled={!canEditDetails}
              className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {opponents.map((opponent) => (
                <option key={opponent._id} value={opponent._id}>
                  {opponent.name}
                </option>
              ))}
            </select>
          </div>
          {/* <div className="flex flex-row gap-3 text-base font-medium"> */}
          {/* <p className='min-w-24 pt-1 pl-2'>Name</p> */}
          <input
            type="text"
            name="name"
            value={opponents.filter((opponent) => opponent._id === gameDetails.opponentId)[0]?.name || ''}
            onChange={handleInputChange}
            hidden
            className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none"
          />
          {/* </div> */}
          <div className="flex flex-row gap-3 text-base font-medium rounded">
            <p className="min-w-24 pt-1 pl-2">Date</p>
            <input
              type="date"
              name="date"
              value={gameDetails.date}
              onChange={handleInputChange}
              disabled={!canEditDetails}
              className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex flex-row gap-3 text-base font-medium">
            <p className="min-w-24 pt-1 pl-2">Time</p>
            <select
              name="time"
              value={gameDetails.time}
              onChange={handleInputChange}
              disabled={!canEditDetails}
              className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">--:-- --</option>
              {Array.from({ length: (23 - 6 + 1) * 4 }, (_, i) => {
                const totalMinutes = 6 * 60 + i * 15;
                const hours24 = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
                const ampm = hours24 < 12 ? 'AM' : 'PM';
                const label = `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                return (
                  <option key={label} value={label}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex flex-row gap-3 text-base font-medium">
            <p className="min-w-24 pt-1 pl-2">Timezone</p>
            <select
              name="timezone"
              value={gameDetails.timezone}
              onChange={handleInputChange}
              disabled={!canEditDetails}
              className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="alaska">US/Alaska</option>
              <option value="central">US/Central</option>
              <option value="eastern">US/Eastern</option>
              <option value="hawaii">US/Hawaii</option>
              <option value="mountain">US/Mountain</option>
              <option value="pacific">US/Pacific</option>
            </select>
          </div>
          <div className="flex flex-row gap-3 text-base font-medium rounded">
            <p className="min-w-24 pt-1 pl-2">Type</p>
            <select
              name="type"
              value={gameDetails.type}
              onChange={handleInputChange}
              disabled={!canEditDetails}
              className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Not Set</option>
              <option value="Regular">Regular</option>
              <option value="Scrimmage">Scrimmage</option>
              {(sessionManager.canAccessPracticeGames() || gameDetails.type === 'Intra Squad') && (
                <option value="Intra Squad">Intra Squad</option>
              )}
              <option value="Conference/League">Conference/League</option>
              <option value="Playoff">Playoff</option>
              <option value="Championship">Championship</option>
            </select>
          </div>
          <div className="flex flex-row gap-3 text-base font-medium rounded">
            <p className="min-w-24 pt-1 pl-2">Location</p>
            <select
              name="location"
              value={gameDetails.location}
              onChange={handleInputChange}
              disabled={!canEditDetails}
              className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Not Set</option>
              <option value="Home">Home</option>
              <option value="Away">Away</option>
              <option value="Neutral">Neutral</option>
            </select>
          </div>
        </div>
        <div className="flex justify-center pb-4">
          <button
            type="submit"
            disabled={loading || !canEditDetails}
            className="bg-sd-accent text-black font-semibold py-2 px-8 rounded text-lg shadow-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            title={!canEditDetails ? "You don't have permission to edit game details" : ''}
          >
            {loading ? 'Saving...' : 'Save Updates'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailsPage;
